import { logEvent as firebaseLogEvent, setUserProperties as firebaseSetUserProperties } from 'firebase/analytics';
import { analytics } from '../firebase/firebase';

/**
 * Log a page view event
 * @param {string} pagePath - The path of the page (e.g., '/roscas')
 * @param {string} pageTitle - The title of the page
 */
export function logPageView(pagePath, pageTitle) {
    if (!analytics) return;
    try {
        firebaseLogEvent(analytics, 'page_view', {
            page_path: pagePath,
            page_title: pageTitle || document.title
        });
    } catch (e) {
        // Silently fail - analytics should not break the app
    }
}

/**
 * Log a custom event
 * @param {string} eventName - The name of the event (use snake_case)
 * @param {Object} params - Event parameters
 */
export function logEvent(eventName, params = {}) {
    if (!analytics) return;
    try {
        firebaseLogEvent(analytics, eventName, params);
    } catch (e) {
        // Silently fail - analytics should not break the app
    }
}

/**
 * Set user properties for segmentation in GA4
 * @param {Object} properties - User properties to set
 */
export function setUserProperties(properties) {
    if (!analytics) return;
    try {
        firebaseSetUserProperties(analytics, properties);
    } catch (e) {
        // Silently fail - analytics should not break the app
    }
}

// Order flow events
export const OrderEvents = {
    BEGIN_ORDER: 'begin_order',
    SUBMIT_ORDER: 'submit_order',
    ORDER_SUCCESS: 'order_success',
    ORDER_ERROR: 'order_error'
};

// Order types
export const OrderTypes = {
    CAKE: 'cake',
    ROSCA: 'rosca',
    BREAD: 'bread',
    CUSTOM: 'custom',
    MERCHANT: 'merchant'
};

/**
 * Log when user starts creating an order
 * @param {string} orderType - Type of order (cake, rosca, bread, custom, merchant)
 */
export function logBeginOrder(orderType) {
    logEvent(OrderEvents.BEGIN_ORDER, { order_type: orderType });
}

/**
 * Log when user submits an order
 * @param {string} orderType - Type of order
 * @param {Object} options - Additional options
 * @param {boolean} options.hasCustomer - Whether customer info was provided
 * @param {boolean} options.hasPrepayment - Whether prepayment was made
 */
export function logSubmitOrder(orderType, options = {}) {
    logEvent(OrderEvents.SUBMIT_ORDER, {
        order_type: orderType,
        has_customer: options.hasCustomer ?? false,
        has_prepayment: options.hasPrepayment ?? false
    });
}

/**
 * Log successful order creation
 * @param {string} orderType - Type of order
 * @param {string} orderId - The created order ID (safe to log)
 */
export function logOrderSuccess(orderType, orderId) {
    logEvent(OrderEvents.ORDER_SUCCESS, {
        order_type: orderType,
        order_id: orderId
    });
}

/**
 * Log failed order creation
 * @param {string} orderType - Type of order
 * @param {string} errorType - Type of error (e.g., 'validation', 'network', 'unknown')
 */
export function logOrderError(orderType, errorType) {
    logEvent(OrderEvents.ORDER_ERROR, {
        order_type: orderType,
        error_type: errorType
    });
}

/**
 * Log customer search
 * @param {boolean} hasResults - Whether search returned results
 * @param {number} resultCount - Number of results
 */
export function logCustomerSearch(hasResults, resultCount = 0) {
    logEvent('search_customer', {
        has_results: hasResults,
        result_count: resultCount
    });
}

/**
 * Log when baker marks an order as ready
 * @param {string} orderType - Type of order
 */
export function logMarkOrderReady(orderType) {
    logEvent('mark_order_ready', { order_type: orderType });
}

/**
 * Log receipt print
 * @param {string} orderType - Type of order
 */
export function logPrintReceipt(orderType) {
    logEvent('print_receipt', { order_type: orderType });
}

/**
 * Log user login
 * @param {string} method - Login method (e.g., 'google')
 */
export function logLogin(method = 'google') {
    logEvent('login', { method });
}

/**
 * Log user logout
 */
export function logLogout() {
    logEvent('logout');
}

const analyticsService = {
    logPageView,
    logEvent,
    setUserProperties,
    logBeginOrder,
    logSubmitOrder,
    logOrderSuccess,
    logOrderError,
    logCustomerSearch,
    logMarkOrderReady,
    logPrintReceipt,
    logLogin,
    logLogout,
    OrderEvents,
    OrderTypes
};

export default analyticsService;
